import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  UserPlus, Loader2, Mail, Copy, Check, Trash2, Users as UsersIcon, Crown, Shield, GraduationCap, Briefcase, Edit3, Lock, LineChart, DollarSign,
} from 'lucide-react';
import { Sidebar, Topbar } from './Dashboard';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { Switch } from '../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import { MemberRate } from '../components/billing/MemberRate';
import { useToast } from '../hooks/use-toast';
import { team, auth, teamPermissions, getStoredUser } from '../api';

const ROLE_META = {
  owner:   { icon: Crown, label: 'Owner',   tone: 'bg-amber-50 text-amber-800 border-amber-100' },
  partner: { icon: Shield, label: 'Partner', tone: 'bg-emerald-50 text-emerald-800 border-emerald-100' },
  manager: { icon: Briefcase, label: 'Manager', tone: 'bg-blue-50 text-blue-800 border-blue-100' },
  trainee: { icon: GraduationCap, label: 'Trainee', tone: 'bg-stone-100 text-stone-700 border-stone-200' },
};

function RoleBadge({ role }) {
  const meta = ROLE_META[role] || ROLE_META.trainee;
  const Icon = meta.icon;
  return (
    <span className={`inline-flex items-center gap-1 text-[11px] font-medium px-2 py-1 rounded-full border ${meta.tone}`}>
      <Icon size={11}/> {meta.label}
    </span>
  );
}

function initials(name) {
  return (name || 'A').split(' ').map((s) => s[0]).slice(0, 2).join('').toUpperCase();
}

export default function Team() {
  const nav = useNavigate();
  const { toast } = useToast();
  const [me, setMe] = useState(getStoredUser());
  const [loading, setLoading] = useState(true);
  const [members, setMembers] = useState([]);
  const [invites, setInvites] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ email: '', name: '', role: 'trainee' });
  const [lastInviteLink, setLastInviteLink] = useState(null);
  const [copied, setCopied] = useState(false);
  const [editMember, setEditMember] = useState(null); // currently editing
  const [editForm, setEditForm] = useState({ name: '', role: '' });
  const [editBusy, setEditBusy] = useState(false);

  const fetchAll = async () => {
    try {
      const [meRes, data] = await Promise.all([auth.me().catch(() => null), team.list()]);
      if (meRes) setMe(meRes);
      setMembers(data.members || []);
      setInvites(data.invites || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!localStorage.getItem('ad_token')) { nav('/login'); return; }
    fetchAll();
    // eslint-disable-next-line
  }, []);

  const canInvite = ['owner', 'partner'].includes(me?.firmRole);
  const isOwner = me?.firmRole === 'owner';

  const sendInvite = async (e) => {
    e.preventDefault();
    setBusy(true);
    setLastInviteLink(null);
    try {
      const inv = await team.invite(form);
      const link = `${window.location.origin}/signup?invite=${inv.code}`;
      setLastInviteLink({ link, email: inv.email, code: inv.code });
      toast({ title: 'Invite ready', description: `Share the link with ${inv.email}.` });
      setForm({ email: '', name: '', role: 'trainee' });
      fetchAll();
    } catch (err) {
      toast({ title: 'Could not create invite', description: err?.response?.data?.detail || 'Try again.', variant: 'destructive' });
    } finally {
      setBusy(false);
    }
  };

  const copyLink = async (link) => {
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      toast({ title: 'Link copied', description: 'Paste it into an email or message.' });
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast({ title: 'Copy failed', description: 'Please copy the link manually.', variant: 'destructive' });
    }
  };

  const cancelInvite = async (id) => {
    if (!window.confirm('Cancel this invite?')) return;
    try {
      await team.cancelInvite(id);
      toast({ title: 'Invite cancelled' });
      fetchAll();
    } catch (err) {
      toast({ title: 'Cancel failed', description: err?.response?.data?.detail || 'Try again.', variant: 'destructive' });
    }
  };

  const removeMember = async (m) => {
    if (!window.confirm(`Remove ${m.name} from the firm? Their open tasks will be reassigned to you.`)) return;
    try {
      await team.removeMember(m.id);
      toast({ title: 'Member removed', description: m.name });
      fetchAll();
    } catch (err) {
      toast({ title: 'Remove failed', description: err?.response?.data?.detail || 'Try again.', variant: 'destructive' });
    }
  };

  const openEditMember = (m) => {
    setEditMember(m);
    setEditForm({ name: m.name || '', role: m.role || '' });
  };

  const saveEditMember = async (e) => {
    e.preventDefault();
    if (!editMember) return;
    const name = (editForm.name || '').trim();
    if (!name) {
      toast({ title: 'Name is required', variant: 'destructive' });
      return;
    }
    setEditBusy(true);
    try {
      const payload = { name };
      // Only owner can change the job title; employees editing themselves cannot
      if (isOwner) payload.role = (editForm.role || '').trim();
      await team.updateMember(editMember.id, payload);
      toast({ title: 'Member updated', description: name });
      setEditMember(null);
      fetchAll();
    } catch (err) {
      toast({ title: 'Update failed', description: err?.response?.data?.detail || 'Try again.', variant: 'destructive' });
    } finally {
      setEditBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-stone-50 grid place-items-center">
        <div className="flex items-center gap-2 text-stone-600"><Loader2 className="animate-spin" size={18}/> Loading your team...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-50 flex">
      <Sidebar activeLabel="Team"/>
      <div className="flex-1 min-w-0">
        <Topbar user={me} firm={me?.firm}/>
        <main className="p-4 lg:p-8 space-y-6">
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
            <div>
              <h1 className="text-2xl lg:text-3xl font-serif font-semibold text-stone-900">Team</h1>
              <p className="mt-1 text-stone-600 text-sm">Invite partners, managers and trainees so tasks can be assigned to real teammates.</p>
            </div>
            {canInvite && (
              <Button onClick={() => { setDialogOpen(true); setLastInviteLink(null); }} className="bg-emerald-800 hover:bg-emerald-900 text-white h-10">
                <UserPlus size={16} className="mr-2"/> Invite teammate
              </Button>
            )}
          </div>

          <div className="rounded-xl bg-white border border-stone-200 overflow-hidden">
            <div className="flex items-center justify-between p-5 border-b border-stone-100">
              <div className="flex items-center gap-2 text-stone-900 font-semibold"><UsersIcon size={18}/> Firm members</div>
              <Badge variant="outline" className="border-stone-300">{members.length}</Badge>
            </div>
            <div className="divide-y divide-stone-100">
              {members.map((m) => {
                const canEditMember = isOwner || m.id === me.id;
                const canTogglePerms = isOwner && m.firmRole !== 'owner' && m.id !== me.id;
                return (
                <div key={m.id} className="p-4 lg:p-5 hover:bg-stone-50">
                  <div className="flex items-center gap-4">
                    <Avatar className="h-11 w-11">
                      <AvatarFallback className="bg-emerald-100 text-emerald-900 font-semibold">{initials(m.name)}</AvatarFallback>
                    </Avatar>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <div className="font-medium text-stone-900">{m.name}</div>
                        {m.id === me.id && <span className="text-[11px] px-2 py-0.5 rounded-full bg-stone-100 text-stone-600">You</span>}
                        <RoleBadge role={m.firmRole || 'trainee'}/>
                        {m.role && <span className="text-[11px] text-stone-500">· {m.role}</span>}
                      </div>
                      <div className="text-sm text-stone-500 truncate">{m.email}</div>
                    </div>
                    <div className="flex items-center gap-1">
                      {isOwner && <MemberRate member={m} onSaved={fetchAll}/>}
                      {canEditMember && (
                        <button onClick={() => openEditMember(m)} className="text-stone-400 hover:text-emerald-800 p-2" title={m.id === me.id ? 'Edit your name' : 'Edit member'} data-testid={`edit-member-${m.id}`}>
                          <Edit3 size={15}/>
                        </button>
                      )}
                      {isOwner && m.id !== me.id && (
                        <button onClick={() => removeMember(m)} className="text-stone-400 hover:text-red-600 p-2" title="Remove member" data-testid={`remove-member-${m.id}`}>
                          <Trash2 size={15}/>
                        </button>
                      )}
                    </div>
                  </div>
                  {canTogglePerms && (
                    <PermissionToggles member={m} onChanged={fetchAll}/>
                  )}
                </div>
                );
              })}
            </div>
          </div>

          <div className="rounded-xl bg-white border border-stone-200 overflow-hidden">
            <div className="flex items-center justify-between p-5 border-b border-stone-100">
              <div className="flex items-center gap-2 text-stone-900 font-semibold"><Mail size={18}/> Pending invites</div>
              <Badge variant="outline" className="border-stone-300">{invites.length}</Badge>
            </div>
            <div className="divide-y divide-stone-100">
              {invites.length === 0 && (
                <div className="p-8 text-center text-sm text-stone-500">No pending invites. Click <strong>Invite teammate</strong> to add someone.</div>
              )}
              {invites.map((i) => {
                const link = `${window.location.origin}/signup?invite=${i.code}`;
                return (
                  <div key={i.id} className="p-4 lg:p-5 flex flex-col sm:flex-row sm:items-center gap-3">
                    <div className="flex items-center gap-4 flex-1 min-w-0">
                      <div className="h-11 w-11 rounded-lg bg-amber-50 text-amber-700 grid place-items-center flex-shrink-0"><Mail size={18}/></div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <div className="font-medium text-stone-900 truncate">{i.email}</div>
                          <RoleBadge role={i.role}/>
                        </div>
                        <div className="text-xs text-stone-500 mt-0.5">Invited by {i.invitedByName || 'you'}</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <Button onClick={() => copyLink(link)} variant="outline" size="sm" className="gap-1.5">
                        <Copy size={14}/> Copy link
                      </Button>
                      {canInvite && (
                        <button onClick={() => cancelInvite(i.id)} className="text-stone-400 hover:text-red-600 p-2" title="Cancel invite">
                          <Trash2 size={15}/>
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </main>
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Invite a teammate</DialogTitle>
          </DialogHeader>
          {!lastInviteLink ? (
            <form onSubmit={sendInvite} className="space-y-4">
              <div>
                <Label htmlFor="invEmail">Work email</Label>
                <Input id="invEmail" required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="themba@yourfirm.co.za" className="mt-1.5"/>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="invName">Name (optional)</Label>
                  <Input id="invName" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Themba Khumalo" className="mt-1.5"/>
                </div>
                <div>
                  <Label>Role</Label>
                  <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                    <SelectTrigger className="mt-1.5"><SelectValue/></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="partner">Partner</SelectItem>
                      <SelectItem value="manager">Manager</SelectItem>
                      <SelectItem value="trainee">Trainee</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="rounded-lg bg-emerald-50 border border-emerald-100 p-3 text-xs text-emerald-900">
                We&apos;ll generate a private invite link you can share by email or chat &mdash; no external email service required.
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
                <Button type="submit" disabled={busy} className="bg-emerald-800 hover:bg-emerald-900 text-white">
                  {busy ? <Loader2 size={16} className="mr-2 animate-spin"/> : null} Generate invite link
                </Button>
              </DialogFooter>
            </form>
          ) : (
            <div className="space-y-4">
              <div className="rounded-lg bg-emerald-50 border border-emerald-100 p-4">
                <div className="flex items-center gap-2 text-emerald-900 font-medium"><Check size={16}/> Invite ready for {lastInviteLink.email}</div>
                <div className="mt-1 text-xs text-emerald-900/80">Share the link below. It works until the teammate signs up or you cancel it.</div>
              </div>
              <div>
                <Label>Invite link</Label>
                <div className="mt-1.5 flex gap-2">
                  <Input readOnly value={lastInviteLink.link} className="font-mono text-xs"/>
                  <Button type="button" onClick={() => copyLink(lastInviteLink.link)} className="bg-emerald-800 hover:bg-emerald-900 text-white flex-shrink-0">
                    {copied ? <><Check size={14} className="mr-1.5"/> Copied</> : <><Copy size={14} className="mr-1.5"/> Copy</>}
                  </Button>
                </div>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => { setLastInviteLink(null); }}>Send another</Button>
                <Button type="button" onClick={() => setDialogOpen(false)} className="bg-stone-900 hover:bg-stone-800 text-white">Done</Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit member dialog */}
      <Dialog open={!!editMember} onOpenChange={(v) => { if (!v) setEditMember(null); }}>
        <DialogContent className="sm:max-w-md" data-testid="edit-member-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Edit3 size={18} className="text-emerald-800"/> Edit team member</DialogTitle>
          </DialogHeader>
          {editMember && (
            <form onSubmit={saveEditMember} className="space-y-4">
              <div className="rounded-lg bg-stone-50 border border-stone-200 p-3 text-xs text-stone-600">
                Updating <span className="font-semibold text-stone-900">{editMember.email}</span>
                {editMember.firmRole && <span className="ml-2 text-stone-500">· {editMember.firmRole}</span>}
              </div>
              <div>
                <Label htmlFor="edit-name">Full name<span className="text-red-600">*</span></Label>
                <Input
                  id="edit-name"
                  value={editForm.name}
                  onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                  required
                  className="mt-1.5"
                  data-testid="edit-member-name"
                />
              </div>
              {isOwner && (
                <div>
                  <Label htmlFor="edit-role">Job title <span className="text-stone-400 font-normal">(free text — e.g. Bookkeeper, Audit Senior)</span></Label>
                  <Input
                    id="edit-role"
                    value={editForm.role}
                    onChange={(e) => setEditForm({ ...editForm, role: e.target.value })}
                    placeholder="Bookkeeper"
                    className="mt-1.5"
                    data-testid="edit-member-role"
                  />
                </div>
              )}
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setEditMember(null)}>Cancel</Button>
                <Button type="submit" disabled={editBusy} className="bg-emerald-800 hover:bg-emerald-900 text-white" data-testid="edit-member-save">
                  {editBusy ? <Loader2 size={14} className="mr-2 animate-spin"/> : <Check size={14} className="mr-2"/>} Save changes
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}


function PermissionToggles({ member, onChanged }) {
  const { toast } = useToast();
  const [perms, setPerms] = useState({
    seeRetainerAmounts: !!member.permissions?.seeRetainerAmounts,
    seeFirmFinancials: !!member.permissions?.seeFirmFinancials,
  });
  const [saving, setSaving] = useState(null); // key currently saving

  const toggle = async (key, value) => {
    const next = { ...perms, [key]: value };
    setPerms(next);
    setSaving(key);
    try {
      await teamPermissions.update(member.id, next);
      toast({ title: 'Permission updated', description: `${member.name}'s access has been updated.` });
      onChanged?.();
    } catch (err) {
      // Revert on failure
      setPerms(perms);
      toast({ title: 'Could not update permission', description: err?.response?.data?.detail || 'Try again.', variant: 'destructive' });
    } finally { setSaving(null); }
  };

  const items = [
    { key: 'seeRetainerAmounts', Icon: DollarSign, label: 'See client retainer amounts', desc: 'When off, retainer amounts show as •••• everywhere' },
    { key: 'seeFirmFinancials', Icon: LineChart, label: 'Open the Financials tab', desc: 'When off, the whole Financials sidebar is hidden' },
  ];

  return (
    <div className="mt-3 ml-[60px] rounded-lg bg-stone-50/70 border border-stone-200/60 p-3" data-testid={`perms-${member.id}`}>
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-stone-500 font-semibold mb-2">
        <Lock size={10}/> Sensitive-info access
      </div>
      <div className="space-y-2">
        {items.map(({ key, Icon, label, desc }) => (
          <div key={key} className="flex items-center gap-3">
            <Icon size={14} className="text-stone-500 flex-shrink-0"/>
            <div className="flex-1 min-w-0">
              <div className="text-xs font-medium text-stone-900">{label}</div>
              <div className="text-[11px] text-stone-500">{desc}</div>
            </div>
            <Switch
              checked={perms[key]}
              onCheckedChange={(v) => toggle(key, !!v)}
              disabled={saving === key}
              data-testid={`perm-${key}-${member.id}`}
            />
          </div>
        ))}
      </div>
    </div>
  );
}
